import { ArrowPathIcon, ExclamationTriangleIcon, TrashIcon } from "@heroicons/react/24/outline"
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/solid"
import classNames from "classnames"
import * as DateFns from "date-fns"
import React, { useState } from "react"

import { faBackward, faForward } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { PencilIcon } from "@heroicons/react/20/solid"
import * as Commands from "../../../../../model/commands"
import { Idea, SourceStatus, WebSource } from "../../../../../model/types"
import api from "../../../../../services/api"
import IdeaBox from "../../../../components/idea_box"
import { useNana } from "../../../../hooks/useNana"
import useFlash from "../../../../hooks/use_flash"
import EditSourceForm from "../edit_source_form"
import SourceStatusTag from "../source_status_tag"

export default function SourceHeader({
  source,
  setOpenSource,
}: {
  source: WebSource | null
  setOpenSource: (source: WebSource | null) => void
}) {
  const { model, commander, hub } = useNana()
  const { setFlash } = useFlash()
  const [editSourceIsOpen, setEditSourceIsOpen] = useState(false)
  const [metadataReprocessing, setMetadataReprocessing] = useState(false)

  const setSourceStatus = (status: SourceStatus) => {
    if (!source) return
    commander.exec(
      new Commands.SetWebSourceFieldCommand({
        id: source.id,
        field: "status",
        value: status,
      })
    )
  }

  const deleteSource = (source: WebSource) => {
    if (window.confirm("Are you sure?")) {
      commander.exec(new Commands.DeleteWebSourceCommand({ id: source.id }))
      setOpenSource(null)
    }
  }

  if (!source) return null

  const reprocessMetadata = async () => {
    setMetadataReprocessing(true)
    try {
      const response = await api().post(`/api/sources/${source.id}/metadata/reprocess`)
      setFlash({
        type: "success",
        text: `Metadata reprocess queued (${response.data.job_id})`,
      })
    } catch {
      setFlash({
        type: "error",
        text: "Could not queue metadata reprocess",
      })
    } finally {
      setMetadataReprocessing(false)
    }
  }

  const goal: Idea | null = source.goalId ? model.getIdea(source.goalId) : null

  return (
    <>
      <div className="p-4 md:p-8">
        <div className="mb-4 flex hidden-if-keyboard gap-2 justify-between pb-4  border-b border-gray-200 dark:border-gray-800">
          <div className="flex gap-2 items-center">
            <SourceStatusTag source={source} />
            {goal && <IdeaBox idea={goal} />}
          </div>
          <div className="flex items-center md:gap-1">
            {source.status == "new" && (
              <button
                onClick={() => setSourceStatus("queued")}
                type="button"
                className="h-7 inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <FontAwesomeIcon icon={faForward} className="h-4 w-3 text-sky-500" />
                Queue
              </button>
            )}
            {source.status == "queued" && (
              <button
                onClick={() => setSourceStatus("new")}
                type="button"
                className="h-7 inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <FontAwesomeIcon icon={faBackward} className="h-4 w-3 text-sky-500" />
                Dequeue
              </button>
            )}
            {(source.status == "new" || source.status == "queued") && (
              <button
                onClick={() => setSourceStatus("read")}
                type="button"
                className="h-7 inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-1.5 whitespace-nowrap text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <CheckCircleIcon className="h-5 w-5 text-sky-500" />
                Mark read
              </button>
            )}
            {source.status == "read" && (
              <button
                onClick={() => setSourceStatus("new")}
                type="button"
                className="h-7 inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-1.5 whitespace-nowrap text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Mark unread
              </button>
            )}
            {editSourceIsOpen && (
              <EditSourceForm source={source} close={() => setEditSourceIsOpen(false)} />
            )}
            <button
              type="button"
              onClick={reprocessMetadata}
              disabled={metadataReprocessing}
              className="h-7 inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-1.5 whitespace-nowrap text-xs text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-200 dark:hover:bg-gray-700"
              data-testid="reprocess-source-metadata-button"
              aria-label="Reprocess metadata"
              title="Reprocess metadata"
            >
              <ArrowPathIcon
                className={classNames("h-4 w-4 text-sky-500", {
                  "animate-spin": metadataReprocessing,
                })}
              />
              <span className="hidden md:inline">Rescan</span>
            </button>
            <button
              type="button"
              onClick={() => setEditSourceIsOpen(true)}
              className="h-7 inline-flex items-center rounded-md px-1.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
              data-testid="edit-source-button"
            >
              <PencilIcon className="h-4 w-4 text-sm" />
            </button>
            <button
              onClick={() => {
                deleteSource(source)
              }}
              className="h-7 inline-flex items-center rounded-md px-1.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <TrashIcon className="h-4 w-4 text-sm" />
            </button>
            <button
              onClick={() => setOpenSource(null)}
              className="h-7 inline-flex items-center rounded-md px-1.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-4 w-4 text-sm" />
            </button>
          </div>
        </div>
        <div className="flex gap-4 justify-between">
          {source.url ? (
            <a
              target="_blank"
              className={classNames("md:text-lg font-medium hover:underline flex-grow-0", {
                "break-all": !source.name,
                "break-words": source.name,
              })}
              href={source.url}
              data-testid="source-header-title"
            >
              {source.name || source.url}
            </a>
          ) : (
            <div
              className={classNames("md:text-lg font-medium flex-grow-0", {
                "break-all": !source.name,
                "break-words": source.name,
              })}
              data-testid="source-header-title"
            >
              {source.name || "Source"}
            </div>
          )}
        </div>
        <div className="mt-4 md:mt-1 text-sm md:flex hidden-if-keyboard gap-2 items-center">
          {source.author ? (
            <div className="mt-1 whitespace-nowrap overflow-x-hidden text-ellipsis">
              {source.author}
            </div>
          ) : null}
          {source.author && (source.publicationDate || source.type) ? (
            <div className="mt-1 hidden md:block text-gray-600 text-lg">&bull;</div>
          ) : null}
          {source.publicationDate ? (
            <div className="mt-1  whitespace-nowrap">
              {DateFns.format(source.publicationDate, "MMM d, yyyy")}
            </div>
          ) : null}
          {source.publicationDate && source.type ? (
            <div className="mt-1 hidden md:block text-gray-600 text-lg">&bull;</div>
          ) : null}
          <div className="mt-1 whitespace-nowrap">{source.type}</div>
          {source.type && source.url ? (
            <div className="mt-1 hidden md:block text-gray-600 text-lg">&bull;</div>
          ) : null}
          {source.url ? (
            <div className="mt-1 whitespace-nowrap hidden md:block">
              {domainFromUrlString(source.url)}
            </div>
          ) : null}
        </div>
        {source.summary ? (
          <div
            className="mt-3 text-sm text-gray-600 dark:text-gray-300"
            data-testid="source-header-summary"
          >
            {source.summary}
          </div>
        ) : null}
        {source.metadataError ? (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none text-amber-500" />
            <div className="min-w-0">
              <div className="font-medium">Metadata extraction failed</div>
              <div className="break-words">{source.metadataError}</div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}

const domainFromUrlString = (url: string) => {
  const urlObj = new URL(url)
  return urlObj.hostname
}

const NotesHelp = () => {
  return (
    <div className="">
      <p>Take two kinds of notes from each source:</p>
      <ol>
        <li>
          <p>
            <strong>Literature notes</strong>: take these as you read. Write down notes on the topic
            of the source, and the contents, but also your own thoughts on the author's ideas.
          </p>
        </li>
        <li>
          <p>
            <strong>Mosaic notes</strong>: from the literature notes extract a set of ideas that can
            be permanently placed into the mosaic.
          </p>
        </li>
      </ol>
      <p>Once you're done, mark the source as Read, which archives it.</p>
    </div>
  )
}

const MosaicNotesHelp = () => {
  return (
    <div className="">
      <p>Here, capture quick short text notes representing ideas from this source.</p>
      <p>
        Don't keep <em>every</em> idea from the source. Look for ideas that are:
      </p>
      <ul>
        <li>New to you.</li>
      </ul>
      <ul>
        <li>Relevant to your goals.</li>
      </ul>
      <ul>
        <li>Interesting and high-energy to you.</li>
      </ul>
      <p>
        Once created here, they can be placed into the Mosaic and written up fully as permanent
        notes.
      </p>
    </div>
  )
}
